import { useState } from "react";
import { Popover, MenuItem, IconButton, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { deleteReport, downloadReport } from "@/services/reports.service";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { showToast } from "@/redux/actions/toast-actions";
import { useAdminUser } from "@/hooks/use-admin-user";
import GenerateReportDialog from "./generate-report-diaog";
import ReportDetailDrawer from "./report-detail-drawer";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PopoverContent = styled("div")(({ theme }) => ({
  minWidth: 180,
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
}));

// Edit/delete eligibility here follows the docs literally: edit is allowed
// "only while awaiting approval", delete "only if the report has not been
// generated". READY/APPROVED read as "generated"; everything else (awaiting
// approval, scheduled, declined, failed, generating) is treated as
// deletable. Worth confirming the exact status list with backend.
const canEditStatus = (status) => status === "AWAITING_APPROVAL";
const canDeleteStatus = (status) => status !== "READY" && status !== "APPROVED";
const canDownloadStatus = (status) => status === "READY" || status === "APPROVED";

const filenameFromDisposition = (disposition) => {
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : null;
};

export function ReportActionsPopover({ report }) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { data: adminUser } = useAdminUser();

  // Edit/delete are creator-only per the docs, read from the logged-in
  // admin's own profile (GET /admin/user/fetch-profile) rather than trusting
  // the row. Approve/reject now live on the unified Approval Management page
  // (src/pages/approval) instead of here.
  const isCreator =
    adminUser?.id != null &&
    report?.createdById != null &&
    String(adminUser.id) === String(report.createdById);

  const canEdit = isCreator && canEditStatus(report.status);
  const canDelete = isCreator && canDeleteStatus(report.status);
  const canDownload = canDownloadStatus(report.status);

  const [anchorEl, setAnchorEl] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const downloadMutation = useMutation({
    mutationFn: () => downloadReport(report.id),
    onSuccess: (response) => {
      const filename =
        filenameFromDisposition(response.headers?.["content-disposition"]) ??
        `${report.name || "report"}.${(report.format || "pdf").toLowerCase()}`;

      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"],
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Failed to download report", "error"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteReport(report.id),
    onSuccess: (data) => {
      dispatch(showToast(data?.message ?? "Report deleted successfully", "success"));
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["reportStats"] });
    },
  });

  const handleDeleteDialogClose = () => {
    if (deleteMutation.isPending) return;
    setDeleteOpen(false);
    deleteMutation.reset();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { elevation: 2, sx: { borderRadius: "8px", mt: 0.5 } },
        }}
      >
        <PopoverContent>
          <StyledMenuItem
            onClick={() => {
              handleClose();
              setDetailOpen(true);
            }}
          >
            View Details
          </StyledMenuItem>

          {canDownload && (
            <StyledMenuItem
              onClick={() => {
                handleClose();
                downloadMutation.mutate();
              }}
              disabled={downloadMutation.isPending}
            >
              {downloadMutation.isPending ? "Downloading..." : "Download Report"}
            </StyledMenuItem>
          )}

          {canEdit && (
            <StyledMenuItem
              onClick={() => {
                handleClose();
                setEditOpen(true);
              }}
            >
              Edit Report
            </StyledMenuItem>
          )}

          {canDelete && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  setDeleteOpen(true);
                }}
                sx={{ color: "error.main" }}
              >
                Delete Report
              </StyledMenuItem>
            </>
          )}
        </PopoverContent>
      </Popover>

      <ReportDetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        reportId={report.id}
      />

      <GenerateReportDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        mode="edit"
        report={report}
      />

      <CustomConfirmDialog
        open={deleteOpen}
        onClose={handleDeleteDialogClose}
        title="Delete Report"
        description={`Are you sure you want to delete "${report.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        mutation={deleteMutation}
        onConfirm={() => deleteMutation.mutate()}
      />
    </>
  );
}
